import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { authApi } from '@/api/authApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

type Stage = 'request' | 'confirm'

// Self-service password reset. Stage 1 asks Cognito to email a code (via the
// backend proxy); stage 2 confirms the code + a new password. On success the
// user returns to /login with a success banner — this flow never mints tokens.
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [stage, setStage] = useState<Stage>('request')
  const [email, setEmail] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // A disabled account routes to /no-access — parity with sign-in behavior.
  function handleError(err: unknown) {
    const message = err instanceof Error ? err.message : 'Something went wrong'
    if (/disabled/i.test(message)) {
      navigate('/no-access', { replace: true })
      return
    }
    setError(message)
  }

  async function requestCode(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const { message } = await authApi.forgotPassword(email.trim())
      setNotice(message)
      setStage('confirm')
    } catch (err) {
      handleError(err)
    } finally {
      setSubmitting(false)
    }
  }

  async function confirmReset(code: string, newPassword: string) {
    setError(null)
    setSubmitting(true)
    try {
      await authApi.confirmForgotPassword(email.trim(), code, newPassword)
      navigate('/login', { replace: true, state: { resetSuccess: true } })
    } catch (err) {
      handleError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-muted/30 px-4'>
      <Card className='w-full max-w-sm'>
        <CardHeader className='items-center text-center'>
          <div className='mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10'>
            <ShieldCheck className='h-6 w-6 text-primary' />
          </div>
          <CardTitle className='text-xl'>Reset your password</CardTitle>
          <CardDescription>
            {stage === 'request'
              ? 'Enter your email and we’ll send a verification code.'
              : 'Enter the code we sent and choose a new password.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stage === 'request' ? (
            <form onSubmit={requestCode} className='flex flex-col gap-4'>
              <div className='flex flex-col gap-1.5'>
                <Label htmlFor='reset-email'>Email or username</Label>
                <Input
                  id='reset-email'
                  type='text'
                  autoComplete='username'
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && (
                <p className='text-sm text-destructive' role='alert'>
                  {error}
                </p>
              )}
              <Button type='submit' disabled={submitting} className='mt-2'>
                {submitting ? 'Sending…' : 'Send reset code'}
              </Button>
              <Link
                to='/login'
                className='text-center text-sm text-muted-foreground hover:underline'
              >
                Back to sign in
              </Link>
            </form>
          ) : (
            <ConfirmResetForm
              notice={notice}
              error={error}
              submitting={submitting}
              onSubmit={confirmReset}
              onBack={() => {
                setStage('request')
                setError(null)
                setNotice(null)
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ConfirmResetForm({
  notice,
  error,
  submitting,
  onSubmit,
  onBack,
}: {
  notice: string | null
  error: string | null
  submitting: boolean
  onSubmit: (code: string, newPassword: string) => void
  onBack: () => void
}) {
  const [code, setCode] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (next !== confirm) {
      setLocalError('Passwords do not match.')
      return
    }
    setLocalError(null)
    onSubmit(code.trim(), next)
  }

  return (
    <form onSubmit={submit} className='flex flex-col gap-4'>
      {notice && <p className='text-sm text-muted-foreground'>{notice}</p>}
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='reset-code'>Verification code</Label>
        <Input
          id='reset-code'
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='reset-new-password'>New password</Label>
        <PasswordInput
          id='reset-new-password'
          autoComplete='new-password'
          required
          minLength={8}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='reset-confirm-password'>Confirm new password</Label>
        <PasswordInput
          id='reset-confirm-password'
          autoComplete='new-password'
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      {(localError || error) && (
        <p className='text-sm text-destructive' role='alert'>
          {localError ?? error}
        </p>
      )}
      <div className='mt-2 flex gap-2'>
        <Button type='button' variant='outline' onClick={onBack} className='flex-1'>
          Back
        </Button>
        <Button type='submit' disabled={submitting || next.length < 8} className='flex-1'>
          {submitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </div>
    </form>
  )
}
